import * as React from 'react';
import type { Toast } from './toast';

/**
 * The toast stack, outside React, and the two hooks that touch it.
 *
 * **It lives apart from `toast.tsx`** because a component file that also exports
 * hooks breaks Vite's Fast Refresh (`react-refresh/only-export-components`):
 * editing `Toaster` or `ToastCard` reloaded the whole page and, along the way,
 * emptied the toast stack being looked at. The same resolution as
 * `company-options.ts` and `lib/aria.ts`.
 *
 * The separation also reflects the split the contract already asked for:
 * `Toaster` is purely presentational (it takes `toasts`+`onDismiss` as props and
 * does not read the store), and this is the store plus the minimum glue.
 */

/* -------------------------------------------------------------------------
 * State: a store outside React, not a Context.
 *
 * `useToast()` receives no `<ToastProvider>` in the contract — it is called from
 * any component, wrapping nothing — so the list lives in a module variable with
 * a minimal pub/sub (`useSyncExternalStore`), the same mechanism the real toast
 * libraries (sonner, react-hot-toast) use for the same reason: "one single
 * Toaster in the whole application" (a decision already taken) implies that
 * emitting and showing are two responsibilities that do not share a parent
 * component.
 * ---------------------------------------------------------------------- */

type Listener = () => void;

let toastList: readonly Toast[] = [];
const listeners = new Set<Listener>();

function notifyListeners(): void {
  for (const listener of listeners) {
    listener();
  }
}

function subscribeToasts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getToastsSnapshot(): readonly Toast[] {
  return toastList;
}

let nextFallbackId = 0;

function createToastId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // jsdom and environments with no Web Crypto: unique within the process is enough.
  nextFallbackId += 1;
  return `toast-${nextFallbackId}`;
}

function addToast(input: Omit<Toast, 'id'>): string {
  const id = createToastId();
  toastList = [...toastList, { ...input, id }];
  notifyListeners();
  return id;
}

function dismissToast(id: string): void {
  toastList = toastList.filter((toast) => toast.id !== id);
  notifyListeners();
}

export interface UseToastResult {
  /** Returns the id, so it can be dismissed later (replacing a «enviando…» once
   * the mutation resolves, say). */
  toast: (t: Omit<Toast, 'id'>) => string;
  dismiss: (id: string) => void;
}

const toastActions: UseToastResult = { toast: addToast, dismiss: dismissToast };

/**
 * Actions for emitting and dismissing toasts from any component. It deliberately
 * does not return the list (contract §5.6): the list's only consumer is the
 * `Toaster` mounted once in `AppShell`, through `useToasterState` (below). If
 * `useToast` also exposed `toasts`, every component that only wants to *emit* a
 * toast would re-render on every change to the whole stack.
 */
export function useToast(): UseToastResult {
  return toastActions;
}

/**
 * For whoever mounts the `Toaster` (today: `AppShell`'s `AppToaster`, and nobody
 * else). It is not in §5.6's contract because that contract describes `Toaster`
 * as a purely presentational component (which is why it takes
 * `toasts`+`onDismiss` as props instead of reading the store itself) — this is
 * the minimum glue between that store and that component:
 *
 * ```tsx
 * function AppToaster() {
 *   const { toasts, onDismiss } = useToasterState();
 *   return <Toaster toasts={toasts} onDismiss={onDismiss} />;
 * }
 * ```
 */
export function useToasterState(): { toasts: readonly Toast[]; onDismiss: (id: string) => void } {
  const toasts = React.useSyncExternalStore(subscribeToasts, getToastsSnapshot, getToastsSnapshot);
  return { toasts, onDismiss: dismissToast };
}
